import { describeApiError, type RoomTemplate } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Spinner } from '@brush/ui';
import { useEffect, useState } from 'react';
import { purposeLabel } from '../../purposes';
import {
  useClearTemplateContent,
  useRoomTemplates,
  useSaveTemplateContent,
} from '../../queries';
import { useRoom } from '../../room';
import { useMainScroll } from '../../scroll';
import { paneIntro, unfilledNote } from '../../templates/pane';
import { useUnsaved } from '../../unsaved';
import { TemplateEditor } from './TemplateEditor';

type State = 'unfilled' | 'incomplete' | 'ready';

/**
 * A template is only usable by this room once the room has filled it in, so the list leads with
 * what is outstanding rather than burying it.
 *
 * `incomplete` is not a stale flag: the platform can add a required field to a template at any
 * time, which invalidates content the room saved without touching it. The server recomputes it
 * against the template as it stands now.
 */
const stateOf = (entry: RoomTemplate): State =>
  entry.content === null ? 'unfilled' : entry.content.isComplete ? 'ready' : 'incomplete';

const ORDER: Record<State, number> = { incomplete: 0, unfilled: 1, ready: 2 };

/**
 * One pane of Settings, so this renders its content and nothing around it — the heading, the
 * sub-navigation and the page margins belong to `SettingsView`.
 */
const EMPTY_TEMPLATES =
  'No templates are available to this room yet. Templates can be limited to rooms running ' +
  'particular features.';

export function TemplatesPane() {
  const { roomId } = useRoom();
  const templates = useRoomTemplates(roomId);
  const saveContent = useSaveTemplateContent(roomId);
  const clearContent = useClearTemplateContent(roomId);
  const { leave } = useUnsaved();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Opening a template from the foot of a long list, and closing it back to one, each replace
  // what the main region is showing with something a different length. See `scroll.tsx`.
  const scrollToTop = useMainScroll();
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, selectedId]);

  if (templates.isLoading) return <Spinner label="Loading templates…" />;
  if (templates.isError) {
    return (
      <ErrorRetry message="Couldn't load templates." onRetry={() => void templates.refetch()} />
    );
  }

  const rows = templates.data ?? [];
  const error = describeApiError(saveContent.error) ?? describeApiError(clearContent.error);

  const selected = selectedId
    ? (rows.find((r) => r.template.id === selectedId) ?? null)
    : null;

  if (selected) {
    return (
      <>
        <ErrorNotice message={error} className="mb-4" />
        <TemplateEditor
          // Remounts when the template changes, so the draft never carries across templates.
          key={selected.template.id}
          entry={selected}
          saving={saveContent.isPending}
          clearing={clearContent.isPending}
          onSave={(body) =>
            saveContent.mutate({ templateId: selected.template.id, body })
          }
          // Clearing is a deliberate write of its own, so leaving on its success asks nothing —
          // the draft it discards is the thing the operator just chose to throw away.
          onClear={() =>
            clearContent.mutate(selected.template.id, {
              onSuccess: () => setSelectedId(null),
            })
          }
          onBack={() => leave(() => setSelectedId(null))}
        />
      </>
    );
  }

  // Outstanding work first: what the room has yet to do is the reason to open this screen.
  const ordered = [...rows].sort(
    (a, b) =>
      ORDER[stateOf(a)] - ORDER[stateOf(b)] ||
      a.template.name.localeCompare(b.template.name),
  );

  return (
    <div>
      <p className="mb-4 max-w-2xl text-sm text-ink-800/60">
        {paneIntro(rows)}
      </p>

      <ErrorNotice message={error} className="mb-4" />

      {/*
       * Two arrangements. The table is `min-w-[36rem]` — 576px, so below `md` it was a
       * sideways scroll of a frame inside a region that only scrolls vertically, and the button
       * that does the work sat off the right-hand edge where nothing pointed at it.
       *
       * Folded, the row leads with the state rather than the name: this is the one list in the
       * product where a row is a job to do, and "Fill in" against a template a room has not
       * supplied is the whole reason locked rule 9 exists. The button keeps its own line at full
       * width so the orange stays attached to the control that acts, never to the text beside it.
       */}
      <ul className="divide-y divide-ink-800/5 rounded-xl border border-ink-800/10 bg-white shadow-sm md:hidden">
        {ordered.map((entry) => {
          const state = stateOf(entry);
          const csv = entry.template.kind === 'csv';
          const usedFor = purposeLabel(entry.template.purpose);
          return (
            <li key={entry.template.id} className="px-4 py-3">
              <p className="font-medium text-ink-950">{entry.template.name}</p>
              <p className="mt-0.5 text-xs text-ink-800/60">
                {csv ? 'CSV' : 'HTML'}
                {usedFor ? ` · ${usedFor}` : ''}
                {' · '}
                {state === 'unfilled'
                  ? (unfilledNote(entry) ? `Not filled in. ${unfilledNote(entry)}` : 'Not filled in')
                  : state === 'incomplete'
                    ? 'Gained a required field'
                    : csv
                      ? `${entry.content?.fieldKeys.length ?? 0} columns`
                      : 'Markup saved'}
              </p>
              <button
                type="button"
                onClick={() => setSelectedId(entry.template.id)}
                className={
                  state === 'ready'
                    ? 'mt-2.5 w-full rounded-lg border border-ink-800/15 py-2 text-xs font-medium text-ink-800'
                    : 'mt-2.5 w-full rounded-lg bg-signal py-2 text-xs font-bold uppercase tracking-wide text-white'
                }
              >
                {state === 'unfilled' ? 'Fill in' : state === 'incomplete' ? 'Fix' : 'Edit'}
              </button>
            </li>
          );
        })}
        {ordered.length === 0 ? (
          <li className="px-6 py-10 text-center text-sm text-ink-800/60">{EMPTY_TEMPLATES}</li>
        ) : null}
      </ul>

      <div className="hidden overflow-x-auto rounded-xl border border-ink-800/10 bg-white shadow-sm md:block">
        <table className="w-full min-w-[36rem] text-left text-sm">
          <thead className="bg-felt-50/50 text-xs uppercase tracking-wide text-ink-800/60">
            <tr>
              <th className="px-6 py-3 font-medium">Template</th>
              <th className="px-6 py-3 font-medium">Type</th>
              <th className="px-6 py-3 font-medium">Your content</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-800/5">
            {ordered.map((entry) => {
              const state = stateOf(entry);
              const csv = entry.template.kind === 'csv';
              const usedFor = purposeLabel(entry.template.purpose);
              return (
                <tr key={entry.template.id} className="hover:bg-felt-50/30">
                  <td className="px-6 py-4 font-medium text-ink-950">
                    {entry.template.name}
                    {/* Why the template exists, where a room can act on knowing it: this is the
                        one the tournament importer will offer, and only once it is filled in. */}
                    {usedFor ? (
                      <span className="mt-0.5 block text-[11px] font-normal uppercase tracking-wide text-ink-800/50">
                        {usedFor}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 text-ink-800/70">{csv ? 'CSV' : 'HTML'}</td>
                  <td className="px-6 py-4 text-ink-800/70">
                    {state === 'unfilled' ? (
                      <span className="text-ink-800/50">
                        Not filled in
                        {/* The players template's exception to locked rule 9 (PI71): unfilled, it still imports. */}
                        {unfilledNote(entry) ? <span className="mt-0.5 block text-xs">{unfilledNote(entry)}</span> : null}
                      </span>
                    ) : state === 'incomplete' ? (
                      <span>This template gained a required field</span>
                    ) : csv ? (
                      `${entry.content?.fieldKeys.length ?? 0} columns`
                    ) : (
                      'Markup saved'
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {/* Orange marks work waiting on the operator and is always attached to
                        the control that does it — never to the status text beside it. */}
                    <button
                      type="button"
                      onClick={() => setSelectedId(entry.template.id)}
                      className={
                        state === 'ready'
                          ? 'rounded-lg border border-ink-800/15 px-3 py-1.5 text-xs font-medium text-ink-800 hover:bg-felt-50'
                          : 'rounded-lg bg-signal px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white'
                      }
                    >
                      {state === 'unfilled' ? 'Fill in' : state === 'incomplete' ? 'Fix' : 'Edit'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {ordered.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-10 text-center text-sm text-ink-800/60">
                  {EMPTY_TEMPLATES}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
